import * as Y from 'yjs'
import { WebsocketProvider } from 'y-websocket'
import { proxy, useSnapshot } from 'valtio'
import { bind } from 'valtio-yjs'
import randColor from 'randomcolor'
import { copy, makeId, sortItemsBy } from './util'

/*

  COMP STATE

*/

const compState = proxy({
  data: {
    elements: [],
    connections: [],
    elMap: {},
  },
})

function updateCompState(input) {
  compState.data = copy(input)
}

function useCompState() {
  const s = useSnapshot(compState)
  return s.data
}

/*

  COLLAB STATE

*/

const collabState = proxy({
  selected: {},
  editing: {},
  interactions: {},
})

function useNodeInteractions(nodeId) {
  const s = useSnapshot(collabState)
  if (s.interactions[nodeId]) {
    return s.interactions[nodeId]
  }
  return null
}

function useSelected(id) {
  const s = useSnapshot(collabState)

  if (s.selected[id]) {
    return s.selected[id]
  }

  return null
}

function useEditing(id) {
  const s = useSnapshot(collabState)

  if (s.editing[id]) {
    return s.editing[id]
  }

  return null
}

function addSelected(id, userId) {
  if (collabState.selected[id]) {
    if (!collabState.selected[id].includes(userId)) {
      console.log(id, userId)
      collabState.selected[id].push(userId)
    }
  } else {
    collabState.selected[id] = [userId]
  }
}

function addEditing(id, userId) {
  if (collabState.editing[id]) {
    if (!collabState.editing[id].includes(userId)) {
      collabState.editing[id].push(userId)
    }
  } else {
    collabState.editing[id] = [userId]
  }
}

function removeSelected(id, userId) {
  if (collabState.selected[id]) {
    if (collabState.selected[id].includes(userId)) {
      collabState.selected[id] = collabState.selected[id].filter(
        (_id) => _id !== userId,
      )
    }
  }
}

function removeEditing(id, userId) {
  if (collabState.editing[id]) {
    if (collabState.editing[id].includes(userId)) {
      collabState.editing[id] = collabState.editing[id].filter(
        (_id) => _id !== userId,
      )
    }
  }
}

function refreshCollabState(currentUserIds) {
  const _selected = {}
  const _editing = {}

  Object.keys(collabState.selected).forEach((id) => {
    if (collabState.selected[id]) {
      _selected[id] = collabState.selected[id].filter(currentUserIds.includes)
    }
  })
  Object.keys(collabState.editing).forEach((id) => {
    if (collabState.editing[id]) {
      _editing[id] = collabState.editing[id].filter(currentUserIds.includes)
    }
  })

  collabState.selected = _selected
  collabState.editing = _editing
}

function resetCollabState() {
  console.log('resetCollabState')
  collabState.selected = {}
  collabState.editing = {}
}

/*

  USER STATE

*/

function createUser(name) {
  const id = `user-${makeId.small()}`
  const color = randColor({
    luminosity: 'bright',
    format: 'hex',
  })

  return {
    id,
    name,
    color,
  }
}

const userState = proxy({
  users: [],
})

function useUserState() {
  const s = useSnapshot(userState)
  return sortItemsBy(copy(s.users), 'name')
}

function useUserById(id) {
  const s = useSnapshot(userState)
  return s.users.find((u) => u.id === id)
}

function storeUser(u) {
  localStorage.setItem('current-user', JSON.stringify(u))
}

function getCurrentUser() {
  const u = localStorage.getItem('current-user')

  if (u) {
    return JSON.parse(u)
  }

  const user = createUser(prompt('First and last name') || 'Rear Admiral')
  storeUser(user)

  return user
}

const User = getCurrentUser()

/*

  SETUP

*/

function init() {
  const doc = new Y.Doc()

  const provider = new WebsocketProvider(
    'wss://demos.yjs.dev',
    'yjs-reactflow-test-1',
    doc,
  )
  const ymapComps = doc.getMap('comp-state')
  bind(compState, ymapComps)

  const ymapCollab = doc.getMap('collab-state')
  bind(collabState, ymapCollab)

  const { awareness } = provider

  awareness.on('change', () => {
    const states = awareness.getStates()
    const userList = Array.from(states.entries()).map((s) => s[1].user)
    userState.users = userList.filter((u) => u.id !== User.id)
    refreshCollabState(userList.map((u) => u.id))
  })

  awareness.setLocalStateField('user', User)
}

export const collaboration = {
  currentUser: User,
  state: compState,
  init,
  update: updateCompState,
  use: useCompState,
  userState,
  useUserState,
  useUserById,
  collabState,
  useNodeInteractions,
  useEditing,
  useSelected,
  addEditing,
  addSelected,
  removeEditing,
  removeSelected,
  refreshCollabState,
  resetCollabState,
}
